import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { useQuery } from '@tanstack/react-query'
import React from 'react'

import { ApiResponse } from '@/api/services/CustomerService'
import { RiskScoreService } from '@/api/services/RiskScoreService'
import { DataTable } from '@/components/UI/DataTable'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { Paginator } from '@/components/UI/Paginator'
import SkeletonRow from '@/components/UI/Skeleton'
import { ErrorPage } from '@/pages/Error'
import { useAppStore } from '@/store'
import { IRiskScore } from '@/types'
import { formatScore, formatScoringDate } from '@/utils/common'

const MODEL_NAME_OPTIONS = ['FARS', 'BARS', 'MACC'] as const

const columns: ITableColumn<IRiskScore>[] = [
  {
    key: 'totalPercentile',
    id: 'totalPercentile',
    label: 'Total Percentile',
    render: value => (
      <StyledText>{typeof value === 'number' ? Math.round(value) : (value ?? '—')}</StyledText>
    ),
  },
  {
    key: 'score',
    id: 'score',
    label: 'Score',
    render: value => <StyledText>{formatScore(value)}</StyledText>,
  },
  {
    key: 'scoringDate',
    id: 'scoringDate',
    label: 'Scoring Date',
    minWidth: "150px",
    render: value => <StyledText>{formatScoringDate(value)}</StyledText>,
  },
  {
    key: 'triggerEvent',
    id: 'triggerEvent',
    label: 'Trigger Event',
    render: (value = '') => <StyledText>{value}</StyledText>,
  },
  {
    key: 'modelName',
    id: 'modelName',
    label: 'Model Name',
    render: (value = '') => <StyledText>{String(value).toUpperCase()}</StyledText>,
  },
  {
    key: 'modelVersion',
    id: 'modelVersion',
    label: 'Model Version',
    render: (value = '') => <StyledText>{value}</StyledText>,
  },
]

export const RiskScore: React.FC = () => {
  const { user, riskPage, riskModelName, setRiskPage, setRiskModelName } = useAppStore()
  const userId = user?.id

  // Avoid carrying filter/pagination state across customers.
  React.useEffect(() => {
    setRiskPage(1)
    setRiskModelName('')
  }, [userId])

  const { data, isLoading, isError } = useQuery<ApiResponse<IRiskScore>>({
    // userId is part of the key so a different customer never reads cached risk scores.
    queryKey: ['risk-scores', userId, riskPage, riskModelName],
    queryFn: () => RiskScoreService.fetchRiskScores({ page: riskPage, modelName: riskModelName }),
    enabled: !!userId,
    placeholderData: prev => prev,
  })

  const handleFilterChange = (event: SelectChangeEvent) => {
    setRiskPage(1)
    setRiskModelName(event.target.value)
  }

  const Header = (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
      <Typography variant="h6">Risk Scores</Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="Filter">
          <FilterAltOutlinedIcon sx={{ fontSize: 20, color: '#344054' }} />
        </Tooltip>
        <Stack gap={0.5}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#344054', lineHeight: '20px' }}>
            By Model Name
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={riskModelName}
              onChange={handleFilterChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Filter by model name' }}
              sx={{ borderRadius: '8px', fontSize: 14, color: '#344054' }}
            >
              <MenuItem value="">All</MenuItem>
              {MODEL_NAME_OPTIONS.map(name => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
    </Box>
  )

  if (!userId || isLoading) {
    return (
      <>
        {Header}
        <SkeletonRow columns={columns} />
      </>
    )
  }

  const riskScores = data?.data ?? []
  const hasRiskScores = riskScores.length > 0

  return (
    <>
      {Header}

      {isError ? (
        <ErrorPage
          title="Unable to Fetch Risk Scores"
          message="We couldn't retrieve risk score data at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        />
      ) : hasRiskScores ? (
        <DataTable<IRiskScore> rows={riskScores} columns={columns} />
      ) : (
        <EmptyContainer>
          <Message>
            {riskModelName
              ? 'No risk scores found for the selected model.'
              : 'No risk scores found for this customer.'}
          </Message>
        </EmptyContainer>
      )}

      {!isError && Number(data?.pagination?.totalRecords ?? 0) > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={2}
          py={1}
          sx={{
            border: '1px solid #E4E7EC',
            borderRadius: '8px',
          }}
        >
          <Paginator
            totalPages={Number(data?.pagination?.totalPages ?? 1)}
            currentPage={riskPage}
            onChange={setRiskPage}
          />
        </Box>
      )}
    </>
  )
}

const StyledText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  textAlign: 'left',
  color: theme.palette.secondary.main,
}))

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: '1px solid #E4E7EC',
  borderRadius: '8px',
  padding: '3rem 0',
})

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
  textAlign: 'center',
}))
